import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { History, X } from 'lucide-react';
import { HistoryTimeline, type HistoryTimelineProps } from './HistoryTimeline';

export interface HistoryDialogProps extends HistoryTimelineProps {
  open: boolean;
  onClose: () => void;
  /** e.g. "History · 2609-000106" — name the record, not just the feature. */
  title: string;
  subtitle?: string;
}

/**
 * A record's history in a dialog.
 *
 * <p>
 * The trail earns a dialog rather than a panel: it answers a question somebody
 * came looking for, so on the page it took permanent space from the records
 * people actually open the screen to read, and its height varied with how busy
 * the record had been. Here it can be as tall as it likes without pushing
 * anything around.
 * </p>
 *
 * <p>
 * Follows the ConfirmDialog conventions — portalled to <c>document.body</c> so
 * no z-index parent can clip it, Escape and backdrop-click both dismiss.
 * </p>
 */
export function HistoryDialog({ open, onClose, title, subtitle, ...timeline }: HistoryDialogProps) {
  // The scrolling element lives here, so the list's "load more on approach"
  // observer has to watch THIS rather than the viewport — from the viewport's
  // point of view the sentinel never moves.
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 pt-16"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="history-dialog-title"
        className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-md bg-white shadow-xl"
      >
        <header className="flex items-start justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-slate-400" />
            <div>
              <h3 id="history-dialog-title" className="text-sm font-semibold text-slate-900">
                {title}
              </h3>
              {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* The list scrolls, the header does not — a long history should not
            push its own title off the top. */}
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          <HistoryTimeline {...timeline} scrollRootRef={scrollRef} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
